// Wiki lint findings — slide-in panel from the right.
// Lists open findings grouped by type; lets the user dismiss or resolve
// each, or jump to the page that triggered it. "Run lint" button at the
// top kicks the pass off (~30s for the contradictions check).

import { useState, useEffect, useCallback } from 'react';
import { X, Loader2, AlertTriangle, Link2, GitMerge, Calendar, Sparkles, Check } from 'lucide-react';
import { coworkApi } from '../api/coworkApi';

type LintType = 'stale' | 'orphan' | 'missing_link' | 'contradiction';
type LintStatus = 'open' | 'resolved' | 'dismissed';

interface Finding {
  id: string;
  type: LintType;
  pageSlug: string | null;
  description: string;
  severity: 'info' | 'warn' | 'error';
  status: LintStatus;
  detectedAt: string;
}

const TYPE_META: Record<LintType, { label: string; icon: React.ComponentType<{ className?: string }>; color: string }> = {
  stale:         { label: 'Stale date',     icon: Calendar,     color: '#f59e0b' },
  orphan:        { label: 'Orphan page',    icon: GitMerge,     color: '#94a3b8' },
  missing_link:  { label: 'Missing link',   icon: Link2,        color: '#dc2626' },
  contradiction: { label: 'Contradiction',  icon: AlertTriangle, color: '#dc2626' },
};

interface WikiLintPanelProps {
  brandId?: string;
  onClose: () => void;
  onJumpToPage: (slug: string) => void;
}

export function WikiLintPanel({ brandId = 'default', onClose, onJumpToPage }: WikiLintPanelProps) {
  const [findings, setFindings] = useState<Finding[] | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const refresh = useCallback(() => {
    coworkApi.wiki.lint(brandId, 'open')
      .then(d => setFindings(d.findings))
      .catch(e => setError(e.message || 'Failed to load findings'));
  }, [brandId]);

  useEffect(() => { refresh(); }, [refresh]);

  const runLint = async (skipContradictions = false) => {
    setRunning(true);
    setError(null);
    try {
      const r = await coworkApi.wiki.runLint({ brandId, skipContradictions });
      setLastResult(`Found ${r.total} new — ${r.stale} stale, ${r.orphan} orphan, ${r.missingLink} broken link, ${r.contradiction} contradictions.`);
      refresh();
    } catch (e) {
      setError((e as Error).message || 'Lint run failed');
    } finally {
      setRunning(false);
    }
  };

  const setStatus = async (id: string, status: LintStatus) => {
    try {
      await coworkApi.wiki.setLintStatus(id, status, brandId);
      setFindings(prev => prev ? prev.filter(f => f.id !== id) : prev);
    } catch (e) {
      setError((e as Error).message || 'Failed to update finding');
    }
  };

  const grouped = findings ? findings.reduce<Record<LintType, Finding[]>>((acc, f) => {
    (acc[f.type] ||= []).push(f);
    return acc;
  }, {} as Record<LintType, Finding[]>) : null;

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30">
      <div className="bg-white w-full max-w-md h-full shadow-2xl flex flex-col">
        <div className="px-4 py-3 border-b border-[#eaecf0] flex items-center justify-between">
          <div>
            <div className="text-[14px] font-semibold text-[#101828]">Wiki health</div>
            <div className="text-[11px] text-[#94a3b8]">Open findings — dismiss or jump to fix</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f8fafc]">
            <X className="w-4 h-4 text-[#94a3b8]" />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-[#eaecf0] flex items-center gap-2">
          <button
            onClick={() => runLint(false)}
            disabled={running}
            className="text-[11px] font-semibold text-white bg-[#be43ff] hover:bg-[#a93ae0] disabled:opacity-50 px-2.5 py-1 rounded flex items-center gap-1.5"
          >
            {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {running ? 'Running…' : 'Run full lint'}
          </button>
          <button
            onClick={() => runLint(true)}
            disabled={running}
            className="text-[11px] text-[#667085] hover:text-[#101828] disabled:opacity-50 px-2 py-1 rounded hover:bg-[#f8fafc]"
            title="Run only the cheap deterministic checks"
          >
            Quick lint
          </button>
        </div>

        {(lastResult || error) && (
          <div className="px-4 py-2 text-[11px] border-b border-[#eaecf0]">
            {error ? (
              <span className="text-[#dc2626]">{error}</span>
            ) : (
              <span className="text-[#15803d]">{lastResult}</span>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-2">
          {!findings ? (
            <div className="flex justify-center py-10"><Loader2 className="w-4 h-4 animate-spin text-[#94a3b8]" /></div>
          ) : findings.length === 0 ? (
            <div className="text-center text-[12px] text-[#94a3b8] py-10">
              <Check className="w-6 h-6 mx-auto mb-2 text-[#16a34a]" />
              No open findings — wiki is clean.
            </div>
          ) : (
            <>
              {(Object.keys(grouped!) as LintType[]).map((t) => {
                const list = grouped![t];
                const meta = TYPE_META[t];
                const Icon = meta.icon;
                return (
                  <div key={t} className="mb-3">
                    <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-wider font-semibold" style={{ color: meta.color }}>
                      <Icon className="w-3 h-3" />
                      {meta.label} <span className="text-[#cbd5e1] normal-case ml-1">· {list.length}</span>
                    </div>
                    {list.map(f => (
                      <div key={f.id} className="px-2 py-2 mx-1 my-1 rounded border border-[#eaecf0] hover:border-[#be43ff]/30 transition-colors">
                        <div className="text-[12px] text-[#344054] mb-1.5">{f.description}</div>
                        <div className="flex items-center justify-between text-[10px] text-[#94a3b8]">
                          <div>
                            {f.pageSlug && (
                              <button
                                onClick={() => onJumpToPage(f.pageSlug!)}
                                className="text-[#be43ff] hover:underline"
                              >
                                {f.pageSlug}
                              </button>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => setStatus(f.id, 'resolved')}
                              className="px-1.5 py-0.5 rounded hover:bg-[#f0fdf4] text-[#15803d]"
                            >
                              Resolve
                            </button>
                            <button
                              onClick={() => setStatus(f.id, 'dismissed')}
                              className="px-1.5 py-0.5 rounded hover:bg-[#f8fafc] text-[#667085]"
                            >
                              Dismiss
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
